import React, { useState, useMemo, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import { trackShare, trackQuizComplete } from "../analytics";
import {
  Box, Typography, Button, Stack, LinearProgress, Chip, TextField,
} from "@mui/material";
import {
  EmojiEvents, School, ArrowForward, Replay,
  CheckCircle, Cancel, PlayArrow, WhatsApp, ContentCopy, Check,
  LocalFireDepartment, Timer, Share, RateReview,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";

const DR_PAW_WHATSAPP = "96170886078";
const DOMAIN          = "https://drpawleb.com";
const OPTION_LETTERS  = ["A", "B", "C", "D", "E"];
const DISPLAY         = '"Cormorant Garamond", Georgia, serif';
const BLUE            = "#1565C0";
const BABY            = "#42A5F5";

// ── Confetti ──────────────────────────────────────────────────────────────────
const confettiFall = keyframes`
  0%   { transform: translateY(-20px) rotate(0deg);    opacity: 1; }
  85%  { opacity: 0.9; }
  100% { transform: translateY(108vh) rotate(700deg);  opacity: 0; }
`;
const confettiDrift = keyframes`
  0%, 100% { margin-left: 0px; }
  30%       { margin-left: 20px; }
  70%       { margin-left: -16px; }
`;
const CONFETTI_COLORS = [
  "#42A5F5","#22C55E","#F59E0B","#EF4444","#A855F7",
  "#EC4899","#14B8A6","#FBBF24","#FB923C","#818CF8",
];
const CONFETTI_PIECES = Array.from({ length: 30 }, (_, i) => {
  const dur = 2.4 + (i % 6) * 0.22;
  return {
    id: i,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    left: `${(i * 3.4 + 1.2) % 98}%`,
    delay: `${(i * 0.072) % 1.9}s`,
    fallDur: `${dur}s`,
    driftDur: `${(dur * 0.55).toFixed(2)}s`,
    width: 7 + (i % 4) * 3,
    height: i % 3 === 0 ? 7 + (i % 4) * 3 : 13 + (i % 4) * 4,
    isCircle: i % 4 === 0,
  };
});
// ─────────────────────────────────────────────────────────────────────────────

interface QuizData {
  title: string;
  questions: {
    text: string;
    options: string[];
    correctIndex: number;
    explanation?: string;
  }[];
}

type Stage = "welcome" | "quiz" | "lead" | "results" | "review";

const DrPawLogo: React.FC<{ small?: boolean }> = ({ small }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.2}>
    <Box sx={{
      width: small ? 32 : 44, height: small ? 32 : 44,
      borderRadius: small ? "9px" : "13px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: small ? ".85rem" : "1.15rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 800, fontSize: small ? "1.2rem" : "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>
      Dr.Paw
    </Typography>
  </Stack>
);

const pad = (n: number) => String(n).padStart(2, "0");

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#37474F", borderRadius: "10px",
    "& fieldset": { borderColor: "rgba(21,101,192,.2)" },
    "&:hover fieldset": { borderColor: BLUE },
    "&.Mui-focused fieldset": { borderColor: BLUE },
  },
  "& .MuiInputLabel-root": { color: "#90A4AE" },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
};

const bgStyle = {
  minHeight: "100vh",
  background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
  display: "flex", flexDirection: "column",
  position: "relative", overflow: "hidden",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
const PublicQuiz: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawData = searchParams.get("data") || "";

  const quizData: QuizData | null = useMemo(() => {
    try { return JSON.parse(decodeURIComponent(atob(rawData))); }
    catch { return null; }
  }, [rawData]);

  const liveCount = useMemo(() => {
    const h = new Date().getHours();
    const m = new Date().getMinutes();
    const base = h >= 8 && h <= 11 ? 28 : h >= 18 && h <= 22 ? 38 : h >= 12 && h <= 14 ? 22 : 10;
    return Math.max(7, base + (m % 9) - 4);
  }, []);

  const questionSocialCounts = useMemo(() =>
    (quizData?.questions ?? []).map((q, i) => {
      const hash = q.text.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
      return 840 + ((hash + i * 137) % 1800);
    }),
  [quizData]);

  // ── All state/refs before any early returns ───────────────────────────────
  const [stage,           setStage]           = useState<Stage>("welcome");
  const [currentQ,        setCurrentQ]        = useState(0);
  const [selectedIdx,     setSelectedIdx]     = useState<number | null>(null);
  const [answered,        setAnswered]        = useState(false);
  const [score,           setScore]           = useState(0);
  const [answers,         setAnswers]         = useState<boolean[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [streak,          setStreak]          = useState(0);
  const [leadName,        setLeadName]        = useState("");
  const [leadPhone,       setLeadPhone]       = useState("");
  const [countdown,       setCountdown]       = useState({ h: 23, m: 59, s: 59 });
  const [copied,          setCopied]          = useState(false);
  const [displayedScore,  setDisplayedScore]  = useState(0);
  const [showConfetti,    setShowConfetti]    = useState(false);
  const countdownEnd = useRef<number>(0);
  const animFrame    = useRef<number>(0);

  useEffect(() => {
    if (stage !== "results") return;
    countdownEnd.current = Date.now() + 24 * 60 * 60 * 1000;
    const timer = setInterval(() => {
      const diff = Math.max(0, countdownEnd.current - Date.now());
      setCountdown({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
      if (diff === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [stage]);

  useEffect(() => {
    if (stage !== "results" || !quizData?.questions?.length) return;
    const target = Math.round((score / quizData.questions.length) * 100);
    setDisplayedScore(0);
    setShowConfetti(true);
    const duration = 1600;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayedScore(Math.round(eased * target));
      if (p < 1) animFrame.current = requestAnimationFrame(tick);
    };
    animFrame.current = requestAnimationFrame(tick);
    const confettiTimer = setTimeout(() => setShowConfetti(false), 5200);
    return () => {
      cancelAnimationFrame(animFrame.current);
      clearTimeout(confettiTimer);
    };
  }, [stage, score, quizData]);

  if (!quizData || !quizData.questions?.length) {
    return (
      <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#90A4AE" }}>Invalid or expired quiz link.</Typography>
      </Box>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const totalQ          = quizData.questions.length;
  const currentQuestion = quizData.questions[currentQ];
  const progress        = (currentQ / totalQ) * 100;
  const pct             = totalQ > 0 ? Math.round((score / totalQ) * 100) : 0;
  const percentile      = Math.min(97, Math.floor(pct * 0.85 + 10));

  const rank =
    pct === 100 ? { title: "Vet Expert",          icon: "🏆", color: "#22C55E",  bg: "rgba(34,197,94,.1)",    border: "rgba(34,197,94,.35)" }
    : pct >= 80  ? { title: "Senior Practitioner", icon: "⭐", color: BLUE,       bg: "rgba(21,101,192,.1)",   border: "rgba(21,101,192,.35)" }
    : pct >= 60  ? { title: "Advanced Learner",    icon: "💪", color: "#7C3AED",  bg: "rgba(124,58,237,.1)",   border: "rgba(124,58,237,.35)" }
    : pct >= 40  ? { title: "Emerging Vet",        icon: "📚", color: "#D97706",  bg: "rgba(217,119,6,.1)",    border: "rgba(217,119,6,.35)"  }
    :              { title: "Foundation Level",    icon: "🌱", color: "#EF4444",  bg: "rgba(239,68,68,.1)",    border: "rgba(239,68,68,.35)"  };

  const motivation =
    pct === 100 ? { emoji: "🏆", title: "Perfect Score!",  msg: "Absolute mastery! You are a true veterinary expert.",          color: "#22C55E" }
    : pct >= 70  ? { emoji: "🌟", title: "Excellent Work!", msg: "Outstanding knowledge. Keep pushing your limits!",             color: BLUE      }
    : pct >= 40  ? { emoji: "💪", title: "Good Effort!",    msg: "You are on the right path. There is always more to learn!",   color: "#D97706" }
    :              { emoji: "🐾", title: "Keep Going!",     msg: "Every expert was once a beginner. Study hard and try again!", color: "#EF4444" };

  const whatsappMsg = encodeURIComponent(
    `Hi Dr.Paw! I just scored ${pct}% on the "${quizData.title}" quiz${leadName ? ` — I'm ${leadName}` : ""}. I'd love to learn more about joining Dr.Paw courses!`
  );
  const whatsappUrl    = `https://wa.me/${DR_PAW_WHATSAPP}?text=${whatsappMsg}`;
  const challengeMsg   = encodeURIComponent(`I just scored ${pct}% on this vet quiz by Dr.Paw! 🐾${leadName ? ` (${leadName})` : ""}\n\nCan you beat my score? 👊\n\n🔗 Try it here: ${window.location.href}`);
  const challengeWaUrl = `https://wa.me/?text=${challengeMsg}`;

  // ── Event handlers ────────────────────────────────────────────────────────
  const handleSelect = (idx: number) => {
    if (answered) return;
    const correct = idx === currentQuestion.correctIndex;
    setSelectedIdx(idx);
    setAnswered(true);
    if (correct) { setScore((s) => s + 1); setStreak((s) => s + 1); }
    else          { setStreak(0); }
    setAnswers((prev) => [...prev, correct]);
    setSelectedAnswers((prev) => [...prev, idx]);
  };

  const handleNext = () => {
    if (currentQ + 1 >= totalQ) { setStage("lead"); return; }
    setCurrentQ((q) => q + 1);
    setSelectedIdx(null);
    setAnswered(false);
  };

  const handleRestart = () => {
    setStage("welcome"); setCurrentQ(0); setSelectedIdx(null);
    setAnswered(false); setScore(0); setAnswers([]); setSelectedAnswers([]);
    setStreak(0); setLeadName(""); setLeadPhone("");
  };

  const handleCopyLink = () => {
    trackShare("quiz", quizData?.title || "");
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleNativeShare = async () => {
    const data = {
      title: `Dr.Paw Quiz — ${pct}% Score`,
      text: `I scored ${pct}% on the "${quizData.title}" vet quiz by Dr.Paw! 🐾 Can you beat me? Try it free:`,
      url: window.location.href,
    };
    if (typeof navigator.share === "function") {
      try { await navigator.share(data); return; } catch {}
    }
    handleCopyLink();
  };

  const Blobs = () => (
    <>
      <Box sx={{ position: "absolute", top: -120, right: -120, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.08) 0%,transparent 65%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 65%)", pointerEvents: "none" }} />
    </>
  );

  // ── Welcome ────────────────────────────────────────────────────────────────
  if (stage === "welcome") return (
    <Box sx={bgStyle}>
      <Blobs />
      <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontWeight: 600, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Veterinary Excellence Platform
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 520, textAlign: "center" }}>
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{
              width: 100, height: 100, borderRadius: "30px", mx: "auto",
              background: "linear-gradient(135deg,#1565C0,#42A5F5)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 12px 40px rgba(21,101,192,.4), 0 0 0 16px rgba(21,101,192,.07)",
            }}>
              <EmojiEvents sx={{ color: "#fff", fontSize: 50 }} />
            </Box>
          </Box>

          <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={0.8} sx={{ mb: 2 }}>
            <Chip label="🔥 Trending — 1,200+ vets tested" sx={{ bgcolor: "rgba(21,101,192,.08)", color: BLUE, fontWeight: 700, fontSize: ".75rem", border: "1px solid rgba(21,101,192,.2)" }} />
            <Chip label={`🟢 ${liveCount} vets online now`} sx={{ bgcolor: "rgba(34,197,94,.08)", color: "#059669", fontWeight: 700, fontSize: ".75rem", border: "1px solid rgba(34,197,94,.2)" }} />
          </Stack>

          <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={0.8} sx={{ mb: 2.5 }}>
            <Chip label={`${totalQ} Questions`}   size="small" sx={{ bgcolor: "rgba(21,101,192,.08)", color: BLUE, fontWeight: 700, fontSize: ".72rem" }} />
            <Chip label="Instant results"         size="small" sx={{ bgcolor: "rgba(34,197,94,.08)", color: "#059669", fontWeight: 700, fontSize: ".72rem" }} />
            <Chip label="Free — No signup"        size="small" sx={{ bgcolor: "rgba(66,165,245,.08)", color: BLUE, fontWeight: 700, fontSize: ".72rem" }} />
          </Stack>

          <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "2rem", md: "2.8rem" }, lineHeight: 1.1, letterSpacing: "-.01em", mb: 2, fontFamily: DISPLAY }}>
            {quizData.title || "Veterinary Quiz"}
          </Typography>

          <Typography sx={{ color: "#546E7A", fontSize: ".95rem", lineHeight: 1.8, mb: 5, maxWidth: 420, mx: "auto" }}>
            Test your knowledge, get instant expert feedback, and share your rank with the community.
          </Typography>

          <Button
            variant="contained" size="large" endIcon={<PlayArrow />}
            onClick={() => setStage("quiz")}
            sx={{
              px: 6, py: 2, borderRadius: "50px",
              textTransform: "none", fontWeight: 800, fontSize: "1.1rem",
              background: "linear-gradient(135deg,#1565C0,#42A5F5)",
              boxShadow: "0 10px 32px rgba(21,101,192,.4)",
              "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-3px)", boxShadow: "0 16px 40px rgba(21,101,192,.5)" },
              transition: "all .25s ease",
            }}
          >
            Start Quiz
          </Button>
          <Typography variant="caption" sx={{ display: "block", mt: 2.5, color: "#90A4AE", fontSize: ".75rem" }}>
            Takes less than 2 minutes • Share your rank to story
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // ── Quiz ───────────────────────────────────────────────────────────────────
  if (stage === "quiz") {
    const isCorrect  = selectedIdx === currentQuestion.correctIndex;
    const correctOpt = currentQuestion.options[currentQuestion.correctIndex];
    return (
      <Box sx={bgStyle}>
        <Box sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <DrPawLogo small />
            <Chip label={`${currentQ + 1} / ${totalQ}`} size="small" sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".78rem" }} />
            <Stack direction="row" spacing={0.5}>
              {answers.map((c, i) => (
                <Box key={i} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c ? "#22C55E" : "#EF4444" }} />
              ))}
              {Array.from({ length: totalQ - answers.length }).map((_, i) => (
                <Box key={`e-${i}`} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "rgba(21,101,192,.15)" }} />
              ))}
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate" value={progress}
            sx={{ height: 5, borderRadius: 99, bgcolor: "rgba(21,101,192,.1)", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#1565C0,#42A5F5)", borderRadius: 99 } }}
          />
        </Box>

        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, py: 3 }}>
          <Box sx={{ width: "100%", maxWidth: 580 }}>
            {streak >= 2 && (
              <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                <Chip
                  icon={<LocalFireDepartment sx={{ fontSize: "1rem !important", color: `${BLUE} !important` }} />}
                  label={streak >= 4 ? `${streak} in a row — you're on fire! 🔥` : `${streak} in a row — keep it up!`}
                  sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".78rem", border: "1px solid rgba(21,101,192,.25)" }}
                />
              </Stack>
            )}

            <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.12)", borderRadius: "22px", boxShadow: "0 4px 20px rgba(21,101,192,.06)", p: { xs: 2.5, md: 3.5 }, mb: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Chip label={`Question ${currentQ + 1}`} size="small" sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".72rem" }} />
                <Chip
                  label={`${(questionSocialCounts[currentQ] ?? 0).toLocaleString()}+ answered`}
                  size="small"
                  sx={{ bgcolor: "rgba(34,197,94,.08)", color: "#059669", fontWeight: 600, fontSize: ".7rem" }}
                />
              </Stack>
              <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1rem", md: "1.2rem" }, lineHeight: 1.55 }}>
                {currentQuestion.text}
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {currentQuestion.options.map((opt, idx) => {
                const isThisCorrect  = idx === currentQuestion.correctIndex;
                const isThisSelected = selectedIdx === idx;
                let bg = "#fff", border = "1px solid rgba(21,101,192,.12)", textClr = "#37474F", badgeBg = "rgba(21,101,192,.07)", badgeClr = BLUE;
                if (answered) {
                  if (isThisCorrect)       { bg = "rgba(34,197,94,.07)";  border = "1px solid rgba(34,197,94,.35)";  textClr = "#065F46";  badgeBg = "rgba(34,197,94,.18)";  badgeClr = "#059669"; }
                  else if (isThisSelected) { bg = "rgba(239,68,68,.07)";  border = "1px solid rgba(239,68,68,.35)";  textClr = "#991B1B";  badgeBg = "rgba(239,68,68,.18)";  badgeClr = "#DC2626"; }
                }
                return (
                  <Box
                    key={idx} onClick={() => handleSelect(idx)}
                    sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 1.8, md: 2 }, borderRadius: "14px", cursor: answered ? "default" : "pointer", background: bg, border, transition: "all .2s ease", "&:hover": answered ? {} : { background: "rgba(21,101,192,.06)", borderColor: "rgba(21,101,192,.3)", transform: "translateX(6px)" } }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: badgeBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {answered && isThisCorrect   ? <CheckCircle sx={{ color: "#059669", fontSize: "1.1rem" }} />
                       : answered && isThisSelected ? <Cancel     sx={{ color: "#DC2626", fontSize: "1.1rem" }} />
                       : <Typography sx={{ color: badgeClr, fontWeight: 800, fontSize: ".8rem" }}>{OPTION_LETTERS[idx]}</Typography>}
                    </Box>
                    <Typography sx={{ color: textClr, fontWeight: answered && isThisCorrect ? 700 : 500, fontSize: ".93rem", flex: 1, lineHeight: 1.4 }}>{opt}</Typography>
                  </Box>
                );
              })}
            </Stack>

            {answered && (
              <Box sx={{ textAlign: "center" }}>
                <Box sx={{ background: isCorrect ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${isCorrect ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.25)"}`, borderRadius: "14px", px: 3, py: 2, mb: isCorrect && !currentQuestion.explanation ? 2.5 : 1.5 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: ".95rem", color: isCorrect ? "#059669" : "#DC2626", mb: isCorrect ? 0 : 0.5 }}>
                    {isCorrect ? "Correct! Well done! 🎉" : "Not quite this time!"}
                  </Typography>
                  {!isCorrect && (
                    <Typography sx={{ color: "#546E7A", fontSize: ".85rem" }}>
                      Correct answer: <strong style={{ color: "#059669" }}>{OPTION_LETTERS[currentQuestion.correctIndex]}) {correctOpt}</strong>
                    </Typography>
                  )}
                </Box>

                {currentQuestion.explanation && (
                  <Box sx={{ background: "rgba(21,101,192,.05)", border: "1px solid rgba(21,101,192,.18)", borderRadius: "14px", px: 2.5, py: 2, mb: 2.5, textAlign: "left" }}>
                    <Typography sx={{ color: BLUE, fontSize: ".75rem", fontWeight: 700, mb: 0.5 }}>💡 Expert Insight</Typography>
                    <Typography sx={{ color: "#37474F", fontSize: ".85rem", lineHeight: 1.7 }}>
                      {currentQuestion.explanation}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained" endIcon={<ArrowForward />} onClick={handleNext}
                  sx={{ px: 4.5, py: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg,#1565C0,#42A5F5)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-2px)" }, transition: "all .2s ease" }}
                >
                  {currentQ + 1 >= totalQ ? "See My Score" : "Next Question"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Lead capture ────────────────────────────────────────────────────────────
  if (stage === "lead") return (
    <Box sx={bgStyle}>
      <Blobs />
      <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}><DrPawLogo /></Box>
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 480 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography sx={{ fontSize: "3.5rem", mb: 1 }}>🎯</Typography>
            <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.8rem", md: "2.3rem" }, letterSpacing: "-.01em", mb: 1, fontFamily: DISPLAY }}>
              Your score is ready!
            </Typography>
            <Typography sx={{ color: "#546E7A", fontSize: ".92rem", lineHeight: 1.75, maxWidth: 360, mx: "auto" }}>
              Enter your details to unlock your results and get a free veterinary tip from Dr.Paw on WhatsApp.
            </Typography>
          </Box>

          <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.15)", borderRadius: "24px", boxShadow: "0 4px 20px rgba(21,101,192,.07)", p: { xs: 3, md: 4 } }}>
            <TextField label="Your name (optional)" value={leadName} onChange={(e) => setLeadName(e.target.value)} fullWidth size="small" sx={{ mb: 2, ...inputSx }} />
            <TextField label="WhatsApp number — get a free tip!" value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} fullWidth size="small" placeholder="+961 XX XXX XXX" sx={{ mb: 3, ...inputSx }} />
            <Button
              fullWidth variant="contained" endIcon={<ArrowForward />}
              onClick={() => { trackQuizComplete(quizData?.title || "", score, totalQ); setStage("results"); }}
              sx={{ py: 1.7, borderRadius: "14px", textTransform: "none", fontWeight: 800, fontSize: "1rem", background: "linear-gradient(135deg,#1565C0,#42A5F5)", boxShadow: "0 6px 20px rgba(21,101,192,.3)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
            >
              See My Results
            </Button>
            <Button fullWidth onClick={() => setStage("results")} sx={{ mt: 1.5, color: "#90A4AE", textTransform: "none", fontSize: ".82rem", "&:hover": { color: "#546E7A", bgcolor: "transparent" } }}>
              Skip for now
            </Button>
          </Box>

          <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 3 }}>
            {["100% free", "No spam ever"].map((t) => (
              <Chip key={t} icon={<CheckCircle sx={{ fontSize: ".85rem !important", color: "#22C55E !important" }} />} label={t} size="small" sx={{ bgcolor: "rgba(34,197,94,.07)", color: "#059669", border: "1px solid rgba(34,197,94,.2)", fontSize: ".72rem" }} />
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  // ── Review all answers ──────────────────────────────────────────────────────
  if (stage === "review") return (
    <Box sx={bgStyle}>
      <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}><DrPawLogo small /></Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 640, mx: "auto", width: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography sx={{ color: "#0A1628", fontWeight: 800, fontSize: { xs: "1.1rem", md: "1.3rem" } }}>
            📖 Answer Review
          </Typography>
          <Button
            size="small" onClick={() => setStage("results")}
            sx={{ color: "#546E7A", textTransform: "none", fontSize: ".82rem", "&:hover": { color: BLUE, bgcolor: "rgba(21,101,192,.06)" } }}
          >
            ← Back to Results
          </Button>
        </Stack>

        <Stack spacing={2.5}>
          {quizData.questions.map((q, i) => {
            const userIdx   = selectedAnswers[i];
            const isCorrect = answers[i];
            return (
              <Box key={i} sx={{
                background: isCorrect ? "rgba(34,197,94,.04)" : "rgba(239,68,68,.04)",
                border: `1px solid ${isCorrect ? "rgba(34,197,94,.18)" : "rgba(239,68,68,.18)"}`,
                borderRadius: "18px", p: { xs: 2.5, md: 3 },
              }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: "8px", flexShrink: 0, bgcolor: isCorrect ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isCorrect ? <CheckCircle sx={{ color: "#059669", fontSize: ".95rem" }} /> : <Cancel sx={{ color: "#DC2626", fontSize: ".95rem" }} />}
                  </Box>
                  <Typography sx={{ color: "#90A4AE", fontSize: ".73rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>
                    Question {i + 1}
                  </Typography>
                </Stack>

                <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".95rem", lineHeight: 1.55, mb: 2 }}>
                  {q.text}
                </Typography>

                <Stack spacing={0.8}>
                  {q.options.map((opt, idx) => {
                    const isThisCorrect  = idx === q.correctIndex;
                    const isThisSelected = idx === userIdx;
                    if (!isThisCorrect && !isThisSelected) return null;
                    return (
                      <Box key={idx} sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        p: 1.5, borderRadius: "10px",
                        background: isThisCorrect ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)",
                        border: `1px solid ${isThisCorrect ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"}`,
                      }}>
                        <Typography sx={{ fontWeight: 800, fontSize: ".78rem", minWidth: 18, color: isThisCorrect ? "#059669" : "#DC2626" }}>
                          {isThisCorrect ? "✓" : "✗"}
                        </Typography>
                        <Typography sx={{ color: isThisCorrect ? "#065F46" : "#991B1B", fontSize: ".88rem", fontWeight: isThisCorrect ? 700 : 500, flex: 1 }}>
                          {OPTION_LETTERS[idx]}) {opt}
                        </Typography>
                        {isThisCorrect && !isThisSelected && (
                          <Typography sx={{ color: "#059669", fontSize: ".72rem", flexShrink: 0 }}>← correct</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                {q.explanation && (
                  <Box sx={{ mt: 2, p: { xs: 1.8, md: 2 }, borderRadius: "12px", background: "rgba(21,101,192,.05)", border: "1px solid rgba(21,101,192,.18)" }}>
                    <Typography sx={{ color: BLUE, fontSize: ".75rem", fontWeight: 700, mb: 0.5 }}>💡 Expert Insight</Typography>
                    <Typography sx={{ color: "#37474F", fontSize: ".85rem", lineHeight: 1.7 }}>{q.explanation}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        <Button
          fullWidth variant="outlined" onClick={() => setStage("results")}
          sx={{ mt: 3, py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "rgba(21,101,192,.25)", color: "#546E7A", "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: "rgba(21,101,192,.05)" } }}
        >
          ← Back to Results
        </Button>
      </Box>
    </Box>
  );

  // ── Results ─────────────────────────────────────────────────────────────────
  const metaTitle = quizData?.title ? `${quizData.title} — Dr.Paw Quiz` : "Veterinary Quiz — Dr.Paw";
  const metaDesc  = quizData?.title
    ? `Test your vet knowledge: "${quizData.title}". Can you score 100%? Challenge your colleagues — Dr.Paw.`
    : "Test your veterinary knowledge with this Dr.Paw quiz. Share your score and challenge your colleagues!";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={bgStyle}>

      {/* Confetti overlay */}
      {showConfetti && (
        <Box sx={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
          {CONFETTI_PIECES.map((p) => (
            <Box
              key={p.id}
              sx={{
                position: "absolute", top: 0, left: p.left,
                width: p.width, height: p.height,
                borderRadius: p.isCircle ? "50%" : "3px",
                bgcolor: p.color,
                animation: `${confettiFall} ${p.fallDur} ${p.delay} ease-in forwards, ${confettiDrift} ${p.driftDur} ${p.delay} ease-in-out infinite`,
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ position: "absolute", top: -80, right: -80, width: 450, height: 450, borderRadius: "50%", background: `radial-gradient(circle,${motivation.color}18 0%,transparent 65%)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -60, left: -60, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.07) 0%,transparent 65%)", pointerEvents: "none" }} />

      <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}><DrPawLogo small /></Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 540, textAlign: "center" }}>

          <Typography sx={{ fontSize: "4.5rem", lineHeight: 1, mb: 1 }}>{motivation.emoji}</Typography>
          <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "2rem", md: "2.7rem" }, letterSpacing: "-.01em", mb: 0.5, fontFamily: DISPLAY }}>
            {leadName ? `${leadName}, ${motivation.title}` : motivation.title}
          </Typography>
          <Typography sx={{ color: "#546E7A", fontSize: ".95rem", mb: 2.5, lineHeight: 1.7 }}>{motivation.msg}</Typography>

          {/* Rank + Percentile */}
          <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
            <Chip label={`${rank.icon} ${rank.title}`} sx={{ bgcolor: rank.bg, color: rank.color, fontWeight: 800, fontSize: ".82rem", border: `1px solid ${rank.border}`, px: 0.5 }} />
            <Chip label={`Better than ${percentile}% of vets 🏅`} sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".8rem", border: "1px solid rgba(21,101,192,.25)" }} />
          </Stack>

          {/* Score card */}
          <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.12)", borderRadius: "24px", boxShadow: "0 4px 20px rgba(21,101,192,.07)", p: 3, mb: 3 }}>
            <Typography sx={{ color: "#90A4AE", fontSize: ".72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", mb: 1 }}>Your Score</Typography>
            <Typography sx={{ color: motivation.color, fontWeight: 700, fontSize: "5rem", lineHeight: 1, mb: 0.5, fontFamily: DISPLAY }}>{displayedScore}%</Typography>
            <Typography sx={{ color: "#546E7A", fontSize: ".88rem", mb: 2.5 }}>{score} correct out of {totalQ} questions</Typography>
            <LinearProgress
              variant="determinate" value={displayedScore}
              sx={{ height: 8, borderRadius: 99, mb: 2.5, bgcolor: "rgba(21,101,192,.1)", "& .MuiLinearProgress-bar": { background: pct >= 70 ? "linear-gradient(90deg,#22C55E,#86EFAC)" : pct >= 40 ? "linear-gradient(90deg,#F59E0B,#FCD34D)" : "linear-gradient(90deg,#EF4444,#FCA5A5)", borderRadius: 99 } }}
            />
            <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={0.8}>
              {answers.map((correct, i) => (
                <Box key={i} sx={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: correct ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)", border: `1.5px solid ${correct ? "rgba(34,197,94,.4)" : "rgba(239,68,68,.4)"}` }}>
                  <Typography sx={{ fontSize: ".72rem", fontWeight: 800, color: correct ? "#059669" : "#DC2626" }}>{i + 1}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Certificate for top performers — dark card kept intentionally for screenshot */}
          {pct >= 70 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ color: "#90A4AE", fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", mb: 1.5, textAlign: "left" }}>
                🏅 Your Achievement Certificate
              </Typography>
              <Box sx={{
                position: "relative",
                background: "linear-gradient(135deg,#0A1628 0%,#0D2137 50%,#0A1628 100%)",
                border: "1px solid rgba(245,158,11,.3)",
                borderRadius: "20px", p: { xs: 3, md: 4 },
                overflow: "hidden", textAlign: "center",
              }}>
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,transparent,#F59E0B,#FCD34D,#F59E0B,transparent)" }} />
                <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,transparent,#F59E0B,#FCD34D,#F59E0B,transparent)" }} />
                <Typography sx={{ color: "rgba(245,158,11,.65)", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".22em", textTransform: "uppercase", mb: 1.5, fontFamily: DISPLAY }}>
                  Certificate of Achievement
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,.35)", fontSize: ".8rem", mb: 0.8 }}>This certifies that</Typography>
                <Typography sx={{ color: "#FCD34D", fontWeight: 700, fontSize: { xs: "1.6rem", md: "2.1rem" }, mb: 0.8, fontFamily: DISPLAY }}>
                  {leadName || "Veterinary Professional"}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: ".85rem", mb: 1.5 }}>has successfully completed</Typography>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", mb: 2.5, px: 2, fontFamily: DISPLAY }}>{quizData.title}</Typography>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={3} sx={{ mb: 2.5 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ color: motivation.color, fontWeight: 900, fontSize: "2.2rem", lineHeight: 1 }}>{pct}%</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.3)", fontSize: ".7rem", mt: 0.3 }}>Score</Typography>
                  </Box>
                  <Box sx={{ width: 1, height: 40, bgcolor: "rgba(255,255,255,.1)" }} />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "1.5rem", lineHeight: 1 }}>{rank.icon}</Typography>
                    <Typography sx={{ color: rank.color, fontWeight: 700, fontSize: ".75rem", mt: 0.3 }}>{rank.title}</Typography>
                  </Box>
                  <Box sx={{ width: 1, height: 40, bgcolor: "rgba(255,255,255,.1)" }} />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ color: "rgba(255,255,255,.6)", fontWeight: 700, fontSize: ".9rem" }}>{score}/{totalQ}</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.3)", fontSize: ".7rem", mt: 0.3 }}>Correct</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                  <Box sx={{ flex: 1, height: 1, bgcolor: "rgba(245,158,11,.18)" }} />
                  <Typography sx={{ color: "rgba(245,158,11,.45)", fontSize: ".7rem", px: 1 }}>Dr.Paw</Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: "rgba(245,158,11,.18)" }} />
                </Stack>
                <Typography sx={{ color: "rgba(255,255,255,.2)", fontSize: ".7rem", mt: 1 }}>
                  {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </Typography>
              </Box>
              <Typography sx={{ color: "#90A4AE", fontSize: ".75rem", mt: 1 }}>
                📸 Screenshot to save your certificate
              </Typography>
            </Box>
          )}

          {/* Screenshot share card — dark inner card kept for sharing purposes */}
          <Box sx={{ background: "rgba(21,101,192,.04)", border: "1px dashed rgba(21,101,192,.15)", borderRadius: "20px", p: { xs: 2.5, md: 3 }, mb: 2 }}>
            <Typography sx={{ color: "#90A4AE", fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", mb: 2 }}>
              📸 Screenshot & share to your story
            </Typography>
            <Box sx={{ background: "linear-gradient(135deg,#0A1628,#102040)", border: "1px solid rgba(66,165,245,.18)", borderRadius: "16px", p: { xs: 2.5, md: 3 }, mb: 1.5 }}>
              <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: ".75rem", mb: 0.5 }}>Dr.Paw Veterinary Quiz</Typography>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem", mb: 1.2 }}>{quizData.title}</Typography>
              <Typography sx={{ color: motivation.color, fontWeight: 900, fontSize: "3rem", lineHeight: 1 }}>{pct}%</Typography>
              <Typography sx={{ color: rank.color, fontWeight: 800, fontSize: ".9rem", mt: 0.5 }}>{rank.icon} {rank.title}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: ".8rem", mt: 0.5 }}>{motivation.emoji} {motivation.title}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,.2)", fontSize: ".7rem", mt: 1.5 }}>drpawleb.com • #DrPawQuiz</Typography>
            </Box>
            <Typography sx={{ color: "#90A4AE", fontSize: ".78rem" }}>Can your followers beat your score?</Typography>
          </Box>

          {/* Instagram hashtag hook */}
          <Box sx={{ background: "rgba(66,165,245,.06)", border: "1px solid rgba(66,165,245,.2)", borderRadius: "12px", px: 2.5, py: 1.2, mb: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Typography sx={{ fontSize: ".85rem" }}>📱</Typography>
            <Typography sx={{ color: "#546E7A", fontSize: ".8rem" }}>
              Post with <strong style={{ color: BLUE }}>#DrPawQuiz</strong> on Instagram — we'll reshare your story!
            </Typography>
          </Box>

          {/* Countdown */}
          <Box sx={{ background: "rgba(21,101,192,.06)", border: "1px solid rgba(21,101,192,.2)", borderRadius: "14px", px: 2.5, py: 1.5, mb: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Timer sx={{ color: BLUE, fontSize: "1.1rem" }} />
            <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: ".88rem" }}>
              Special offer expires in{" "}
              <strong style={{ fontFamily: DISPLAY, fontSize: "1.15rem", letterSpacing: ".02em" }}>
                {pad(countdown.h)}:{pad(countdown.m)}:{pad(countdown.s)}
              </strong>
            </Typography>
          </Box>

          {/* Primary CTA */}
          <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.15)", borderRadius: "22px", boxShadow: "0 4px 20px rgba(21,101,192,.07)", p: { xs: 2.5, md: 3 }, mb: 2.5, textAlign: "left" }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: "12px", background: "linear-gradient(135deg,#1565C0,#42A5F5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <School sx={{ color: "#fff", fontSize: "1.2rem" }} />
              </Box>
              <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1rem" }}>Ready to go further?</Typography>
            </Stack>
            <Typography sx={{ color: "#546E7A", fontSize: ".86rem", lineHeight: 1.75, mb: 2.5 }}>
              Expert-led courses covering every skill tested in this quiz — and hundreds more. Trusted by veterinary professionals across the Middle East.
            </Typography>
            <Button
              variant="contained" href={`${DOMAIN}/#pricing`} endIcon={<ArrowForward />} fullWidth
              sx={{ py: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg,#1565C0,#42A5F5)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-2px)" }, transition: "all .2s ease", mb: 1.5 }}
            >
              Join Dr.Paw — Start Learning
            </Button>
            <Button
              variant="outlined" href={whatsappUrl} target="_blank" startIcon={<WhatsApp />} fullWidth
              sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" } }}
            >
              Chat with Dr.Paw on WhatsApp
            </Button>
          </Box>

          {/* Social sharing */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="contained" startIcon={<Share />} onClick={handleNativeShare} fullWidth
              sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, fontSize: ".9rem", background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 16px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
            >
              Share Your Score to Story
            </Button>
            <Button
              variant="outlined" startIcon={<WhatsApp />} href={challengeWaUrl} target="_blank" fullWidth
              sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" } }}
            >
              Challenge a Friend on WhatsApp
            </Button>
            <Button
              variant="outlined" startIcon={<RateReview />} onClick={() => setStage("review")} fullWidth
              sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: "rgba(21,101,192,.25)", color: "#546E7A", "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: "rgba(21,101,192,.05)" } }}
            >
              Review All Answers
            </Button>
          </Stack>

          {/* Copy + Retry */}
          <Stack spacing={1} direction={{ xs: "column", sm: "row" }}>
            <Button
              variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopyLink} fullWidth
              sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", "&:hover": { borderColor: copied ? "rgba(34,197,94,.5)" : BLUE, color: copied ? "#22C55E" : BLUE }, transition: "all .2s ease" }}
            >
              {copied ? "Link Copied!" : "Copy Link"}
            </Button>
            <Button
              variant="outlined" startIcon={<Replay />} onClick={handleRestart} fullWidth
              sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: "rgba(21,101,192,.25)", color: "#78909C", bgcolor: "#fff", "&:hover": { borderColor: BLUE, color: BLUE } }}
            >
              Try Again
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicQuiz;
